import React, {useContext, useMemo, useState} from "react";
import "./ContentResumeDeclaration.scss";
import {EventUpdatedContext} from "../../../../store/EventUpdatedContext";
import {useFetchDeclarationResume} from "../../../../service/Business/DeclarationService";
import {getIdWithToken} from "../../../../utils/storage/Token";
import FCSpinner from "../../../FCObjets/FCSpinner";
import {DeclarationResumeInterface, DeclarationTVAResumeInterface} from "../../../../service/Business/Interfaces/DeclarationInterface";
import FCTag from "../../../FCObjets/FCTag";
import StatusDeclaration from "../../../../utils/Business/StatusDeclaration";
import FCContentModalFL, {DataForm} from "../../../FCObjets/Form/FCContentModalFL";
import {TypeForm} from "../../../../types/EnumTypeForm";
import TypeDate from "../../../../types/TypeDate";
import FCSortableHeader from "../../../FCObjets/FCSortableHeader";
import {useTableSort} from "../../../../hook/useTableSort";
import formatEuro from "../../../../utils/formatEuro";

type ResumeUrssafSortKey = "desc" | "date" | "ca" | "cfe" | "cpbnc" | "vl" | "total" | "state";
type ResumeTvaSortKey = "month" | "ca" | "vatMonth" | "vatSum" | "vatDeclared" | "state";

function ContentResumeDeclaration() {

    const [year, setYear] = useState(new Date().getFullYear());
    const {cptUpdated, setUpdated} = useContext(EventUpdatedContext);
    const {data: resume, loading, error} = useFetchDeclarationResume(getIdWithToken(), year, cptUpdated);

    const [data, setData] = useState<DataForm>({
        type: TypeForm.EMPTY,
        data: undefined,
    });

    const gestionModal = (open: boolean, e: DeclarationResumeInterface | any | undefined, type: TypeForm | undefined) => {
        setData({
            type: (open && type) ? type : TypeForm.EMPTY,
            data: e
        });
    };

    const urssafSort = useTableSort<DeclarationResumeInterface, ResumeUrssafSortKey>(
        resume?.resume_urssaf ?? [],
        {
            desc: (e) => e.desc,
            date: (e) => new Date(e.date),
            ca: (e) => e.ca,
            cfe: (e) => e.cfe,
            cpbnc: (e) => e.cpbnc,
            vl: (e) => e.vl,
            total: (e) => e.total_urssaf,
            state: (e) => e.state,
        },
    );

    const tvaSort = useTableSort<DeclarationTVAResumeInterface, ResumeTvaSortKey>(
        resume?.resume_tva ?? [],
        {
            month: (e) => e.month,
            ca: (e) => e.tva.ca,
            vatMonth: (e) => e.tva.vatMonth,
            vatSum: (e) => e.tva.vatSum,
            vatDeclared: (e) => e.tva.vatDeclared,
            state: (e) => e.state,
        },
    );

    const stats = useMemo(() => {
        if (!resume) return {totalCA: 0, totalUrssaf: 0, totalTVA: 0};
        const totalCA = resume.resume_urssaf.reduce((acc: number, d: DeclarationResumeInterface) => acc + d.ca, 0);
        const totalUrssaf = resume.resume_urssaf.reduce((acc: number, d: DeclarationResumeInterface) => acc + d.total_urssaf, 0);
        const totalTVA = resume.resume_tva.reduce((acc: number, d: DeclarationTVAResumeInterface) => acc + Math.round(d.tva.vatDeclared), 0);
        return {totalCA, totalUrssaf, totalTVA};
    }, [resume]);

    return (
        <FCContentModalFL form={data} onModalFormFinished={() => {
            gestionModal(false, undefined, undefined);
            setUpdated();
        }}>
            <div className="resumeDeclPage">
                {/* Header */}
                <div className="resumeDeclHeader">
                    <div>
                        <h1 className="resumeDeclTitle">Résumé des déclarations</h1>
                        <p className="resumeDeclSubtitle">Vue d'ensemble de vos obligations fiscales et sociales</p>
                    </div>
                    <div className="resumeDeclHeaderRight">
                        <div className="resumeDeclYearNav">
                            <button className="resumeDeclYearBtn" onClick={() => setYear(year - 1)}>
                                <span className="material-symbols-outlined">chevron_left</span>
                            </button>
                            <span className="resumeDeclYear">{year}</span>
                            <button className="resumeDeclYearBtn" onClick={() => setYear(year + 1)}>
                                <span className="material-symbols-outlined">chevron_right</span>
                            </button>
                        </div>
                    </div>
                </div>

                {loading && <div className="resumeDeclLoading"><FCSpinner size="middle"/></div>}
                {error && <div className="resumeDeclEmptyState">{error.message}</div>}

                {resume && (
                    <>
                        {/* KPI Cards */}
                        <div className="resumeDeclKpiGrid">
                            <div className="resumeDeclKpiCard resumeDeclKpiCA">
                                <div className="resumeDeclKpiLabel">Chiffre d'affaires</div>
                                <div className="resumeDeclKpiValueLg">{formatEuro(stats.totalCA)}</div>
                                <div className="resumeDeclKpiSub">CA total déclaré {year}</div>
                            </div>
                            <div className="resumeDeclKpiCard">
                                <div className="resumeDeclKpiLabel">Cotisations URSSAF</div>
                                <div className="resumeDeclKpiValue">{formatEuro(stats.totalUrssaf)}</div>
                                <div className="resumeDeclKpiSub">Total cotisations sociales</div>
                            </div>
                            <div className="resumeDeclKpiCard">
                                <div className="resumeDeclKpiLabel">TVA nette due</div>
                                <div className="resumeDeclKpiValue">{formatEuro(stats.totalTVA)}</div>
                                <div className="resumeDeclKpiSub">Total TVA {year}</div>
                            </div>
                        </div>

                        {/* URSSAF Table */}
                        <h2 className="resumeDeclSectionTitle">URSSAF {year}</h2>

                        {/* Desktop table */}
                        <div className="resumeDeclTableWrapper">
                            <table className="resumeDeclTable">
                                <thead>
                                <tr>
                                    <th><FCSortableHeader label="Description" sortKey="desc" sortState={urssafSort.sortState} onSort={urssafSort.handleSort}/></th>
                                    <th><FCSortableHeader label="Date" sortKey="date" sortState={urssafSort.sortState} onSort={urssafSort.handleSort}/></th>
                                    <th><FCSortableHeader label="CA" sortKey="ca" sortState={urssafSort.sortState} onSort={urssafSort.handleSort}/></th>
                                    <th><FCSortableHeader label="CFE" sortKey="cfe" sortState={urssafSort.sortState} onSort={urssafSort.handleSort}/></th>
                                    <th><FCSortableHeader label="CPBNC" sortKey="cpbnc" sortState={urssafSort.sortState} onSort={urssafSort.handleSort}/></th>
                                    <th><FCSortableHeader label="VL" sortKey="vl" sortState={urssafSort.sortState} onSort={urssafSort.handleSort}/></th>
                                    <th><FCSortableHeader label="Total URSSAF" sortKey="total" sortState={urssafSort.sortState} onSort={urssafSort.handleSort}/></th>
                                    <th><FCSortableHeader label="Statut" sortKey="state" sortState={urssafSort.sortState} onSort={urssafSort.handleSort}/></th>
                                    <th>Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {urssafSort.sortedData.map((e: DeclarationResumeInterface, index: React.Key) => (
                                    <tr key={index}>
                                        <td><span className="resumeDeclCellDesc">{e.desc}</span></td>
                                        <td>{new Date(e.date).toLocaleDateString()}</td>
                                        <td><span className="resumeDeclCellAmount">{formatEuro(e.ca)}</span></td>
                                        <td>{formatEuro(e.cfe)}</td>
                                        <td>{formatEuro(e.cpbnc)}</td>
                                        <td>{formatEuro(e.vl)}</td>
                                        <td><span className="resumeDeclCellAmountGold">{formatEuro(e.total_urssaf)}</span></td>
                                        <td>
                                            <FCTag value={StatusDeclaration.toString(e.state)}
                                                   color={StatusDeclaration.toColor(e.state)}
                                                   style={{fontWeight: "bold"}}/>
                                        </td>
                                        <td>
                                            <div className="resumeDeclCellActions">
                                                <button className="resumeDeclActionBtn"
                                                        onClick={() => gestionModal(true, e, TypeForm.STATE_DECLARATION_URSSAF)}
                                                        title="Modifier">
                                                    <span className="material-symbols-outlined">edit</span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>

                        {/* URSSAF Mobile cards */}
                        <div className="resumeDeclMobileList">
                            {urssafSort.sortedData.map((e: DeclarationResumeInterface, index: React.Key) => (
                                <div className="resumeDeclMobileCard" key={index}>
                                    <div className="resumeDeclMobileCardHeader">
                                        <span className="resumeDeclMobileCardTitle">{e.desc}</span>
                                        <FCTag value={StatusDeclaration.toString(e.state)}
                                               color={StatusDeclaration.toColor(e.state)}
                                               style={{fontWeight: "bold"}}/>
                                    </div>
                                    <div className="resumeDeclMobileCardGrid">
                                        <div className="resumeDeclMobileCardItem">
                                            <span className="resumeDeclMobileCardItemLabel">CA</span>
                                            <span className="resumeDeclMobileCardItemValue">{formatEuro(e.ca)}</span>
                                        </div>
                                        <div className="resumeDeclMobileCardItem">
                                            <span className="resumeDeclMobileCardItemLabel">Total URSSAF</span>
                                            <span className="resumeDeclMobileCardItemValueGold">{formatEuro(e.total_urssaf)}</span>
                                        </div>
                                        <div className="resumeDeclMobileCardItem">
                                            <span className="resumeDeclMobileCardItemLabel">CFE</span>
                                            <span className="resumeDeclMobileCardItemValue">{formatEuro(e.cfe)}</span>
                                        </div>
                                        <div className="resumeDeclMobileCardItem">
                                            <span className="resumeDeclMobileCardItemLabel">CPBNC</span>
                                            <span className="resumeDeclMobileCardItemValue">{formatEuro(e.cpbnc)}</span>
                                        </div>
                                    </div>
                                    <div className="resumeDeclMobileCardActions">
                                        <button className="resumeDeclMobileActionBtn"
                                                onClick={() => gestionModal(true, e, TypeForm.STATE_DECLARATION_URSSAF)}>
                                            <span className="material-symbols-outlined">edit</span>
                                            Modifier
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* TVA Table */}
                        <h2 className="resumeDeclSectionTitle">TVA {year}</h2>

                        {/* Desktop table */}
                        <div className="resumeDeclTableWrapper">
                            <table className="resumeDeclTable">
                                <thead>
                                <tr>
                                    <th><FCSortableHeader label="Mois" sortKey="month" sortState={tvaSort.sortState} onSort={tvaSort.handleSort}/></th>
                                    <th><FCSortableHeader label="CA à déclarer" sortKey="ca" sortState={tvaSort.sortState} onSort={tvaSort.handleSort}/></th>
                                    <th><FCSortableHeader label="TVA brute due" sortKey="vatMonth" sortState={tvaSort.sortState} onSort={tvaSort.handleSort}/></th>
                                    <th><FCSortableHeader label="TVA déductible" sortKey="vatSum" sortState={tvaSort.sortState} onSort={tvaSort.handleSort}/></th>
                                    <th><FCSortableHeader label="TVA nette due" sortKey="vatDeclared" sortState={tvaSort.sortState} onSort={tvaSort.handleSort}/></th>
                                    <th><FCSortableHeader label="Statut" sortKey="state" sortState={tvaSort.sortState} onSort={tvaSort.handleSort}/></th>
                                    <th>Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {tvaSort.sortedData.map((e: DeclarationTVAResumeInterface, index: React.Key) => (
                                    <tr key={index}>
                                        <td><span className="resumeDeclCellDesc">{TypeDate.monthsLong[e.month]}</span></td>
                                        <td><span className="resumeDeclCellAmount">{formatEuro(e.tva.ca)}</span></td>
                                        <td>{formatEuro(Math.round(e.tva.vatMonth))}</td>
                                        <td>{formatEuro(Math.round(e.tva.vatSum))}</td>
                                        <td><span className="resumeDeclCellAmountGold">{formatEuro(Math.round(e.tva.vatDeclared))}</span></td>
                                        <td>
                                            <FCTag value={StatusDeclaration.toString(e.state)}
                                                   color={StatusDeclaration.toColor(e.state)}
                                                   style={{fontWeight: "bold"}}/>
                                        </td>
                                        <td>
                                            <div className="resumeDeclCellActions">
                                                <button className="resumeDeclActionBtn"
                                                        onClick={() => gestionModal(true, e, TypeForm.STATE_DECLARATION_TVA)}
                                                        title="Modifier">
                                                    <span className="material-symbols-outlined">edit</span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>

                        {/* TVA Mobile cards */}
                        <div className="resumeDeclMobileList">
                            {tvaSort.sortedData.map((e: DeclarationTVAResumeInterface, index: React.Key) => (
                                <div className="resumeDeclMobileCard" key={index}>
                                    <div className="resumeDeclMobileCardHeader">
                                        <span className="resumeDeclMobileCardTitle">{TypeDate.monthsLong[e.month]}</span>
                                        <FCTag value={StatusDeclaration.toString(e.state)}
                                               color={StatusDeclaration.toColor(e.state)}
                                               style={{fontWeight: "bold"}}/>
                                    </div>
                                    <div className="resumeDeclMobileCardGrid">
                                        <div className="resumeDeclMobileCardItem">
                                            <span className="resumeDeclMobileCardItemLabel">CA</span>
                                            <span className="resumeDeclMobileCardItemValue">{formatEuro(e.tva.ca)}</span>
                                        </div>
                                        <div className="resumeDeclMobileCardItem">
                                            <span className="resumeDeclMobileCardItemLabel">TVA nette</span>
                                            <span className="resumeDeclMobileCardItemValueGold">{formatEuro(Math.round(e.tva.vatDeclared))}</span>
                                        </div>
                                        <div className="resumeDeclMobileCardItem">
                                            <span className="resumeDeclMobileCardItemLabel">TVA brute</span>
                                            <span className="resumeDeclMobileCardItemValue">{formatEuro(Math.round(e.tva.vatMonth))}</span>
                                        </div>
                                        <div className="resumeDeclMobileCardItem">
                                            <span className="resumeDeclMobileCardItemLabel">TVA déductible</span>
                                            <span className="resumeDeclMobileCardItemValue">{formatEuro(Math.round(e.tva.vatSum))}</span>
                                        </div>
                                    </div>
                                    <div className="resumeDeclMobileCardActions">
                                        <button className="resumeDeclMobileActionBtn"
                                                onClick={() => gestionModal(true, e, TypeForm.STATE_DECLARATION_TVA)}>
                                            <span className="material-symbols-outlined">edit</span>
                                            Modifier
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </FCContentModalFL>
    );
}

export default ContentResumeDeclaration;
